/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// LOCAL STORAGE SAVED VIEWS ADAPTER (INTERNAL)
// ============================================================================
//
// ⚠️ DEPRECATED AS PUBLIC API - Use useSavedViewsUnified instead
//
// This hook is now an internal adapter used by useSavedViewsUnified.
// It should NOT be imported directly in page components.
//
// **Migration:**
// - Replace: import { useLocalSavedViewsAdapter } from "@/hooks/ui"
// - With: import { useSavedViewsUnified } from "@/hooks/ui"
// - Add storageKey parameter (required for local storage types)
//
// This hook will remain as an internal implementation detail for local storage
// saved views (suppliers, cost-profiles, services, documents).
//
// ============================================================================

"use client";

import { logger } from "@/lib/utils/logger";
import { getPlatformPresets } from "@/lib/utils/saved-views/presets";
import type { NormalizedSavedView } from "@/types/saved-views";
import { normalizeLocalSavedView } from "@/types/saved-views";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { SavedViewType } from "../";
import { useUrlState } from "../../state";
import { useSavedViews } from "./use-saved-views";

export interface UseLocalSavedViewsAdapterOptions<TFilters> {
  /**
   * View type identifier
   */
  viewType: SavedViewType;

  /**
   * Storage key for local storage
   */
  storageKey: string;

  /**
   * Current filter state for comparison (determines dirty state)
   */
  currentFilters?: TFilters;

  /**
   * Callback to apply filters from a saved view
   */
  onApplyView?: (filters: TFilters) => void;

  /**
   * Default/reset filters to use when clearing a view (handleApplyView(null))
   * If not provided, an empty object {} will be used
   */
  defaultFilters?: TFilters;

  /**
   * Whether to sync selected view ID with URL (default: true)
   */
  syncWithUrl?: boolean;

  /**
   * Whether to auto-apply default view on mount (default: false)
   */
  autoApplyDefault?: boolean;

  /**
   * Whether the hook is enabled (default: true)
   */
  enabled?: boolean;
}

/**
 * Adapter hook that provides unified saved views interface using local storage
 *
 * ⚠️ DEPRECATED AS PUBLIC API - Use useSavedViewsUnified instead
 *
 * This hook is now an internal adapter used by useSavedViewsUnified for
 * local storage view types (suppliers, cost-profiles, services, documents).
 *
 * **Migration:**
 * ```tsx
 * // Old (deprecated)
 * const { views, ... } = useLocalSavedViewsAdapter({
 *   viewType: "suppliers",
 *   storageKey: "suppliers",
 *   ...
 * });
 *
 * // New (recommended)
 * const { views, ... } = useSavedViewsUnified({
 *   viewType: "suppliers",
 *   storageKey: "suppliers", // Required for local storage types
 *   ...
 * });
 * ```
 *
 * The API is identical - just rename the hook import and call.
 *
 * @deprecated Use useSavedViewsUnified instead. This hook remains as an internal implementation detail.
 */
export function useLocalSavedViewsAdapter<
  TFilters extends Record<string, unknown>,
>(options: UseLocalSavedViewsAdapterOptions<TFilters>) {
  const {
    viewType,
    storageKey,
    currentFilters,
    onApplyView,
    defaultFilters,
    syncWithUrl = true,
    autoApplyDefault = false,
    enabled = true,
  } = options;

  // Sync selected view ID with URL
  const { state: urlState, setUrlState } = useUrlState<{ viewId?: string }>(
    ["viewId"],
    { enabled: syncWithUrl },
  );

  const selectedViewIdFromUrl =
    syncWithUrl && typeof urlState.viewId === "string" ? urlState.viewId : "";

  // Track if we're currently applying a view to prevent loops
  const applyingViewRef = useRef(false);

  // Use local storage saved views hook
  const {
    views: localViews,
    upsertView,
    removeView,
  } = useSavedViews<TFilters>({
    storageKey,
  });

  // Get platform presets for view types that have them
  const platformPresets = useMemo(() => {
    if (
      viewType === "projects" ||
      viewType === "clients" ||
      viewType === "contacts" ||
      viewType === "estimates"
    ) {
      return getPlatformPresets<TFilters>(viewType);
    }
    return [];
  }, [viewType]);

  // Normalize local views to unified format and prepend platform presets
  const allViews = useMemo<NormalizedSavedView<TFilters>[]>(() => {
    const normalized = localViews.map((view) =>
      normalizeLocalSavedView(view, viewType),
    );
    return [...platformPresets, ...normalized];
  }, [localViews, viewType, platformPresets]);

  // Get currently selected view
  const selectedView = useMemo(
    () => allViews.find((view) => view.id === selectedViewIdFromUrl),
    [allViews, selectedViewIdFromUrl],
  );

  // Check if current filters differ from selected view (dirty state)
  const isDirty = useMemo(() => {
    if (!currentFilters) {
      return false;
    }

    if (!selectedView) {
      // If no view selected, compare against defaultFilters
      // If defaultFilters not provided, consider dirty if we have any active filters
      if (defaultFilters) {
        // Deep equality check against defaultFilters
        return (
          JSON.stringify(defaultFilters) !== JSON.stringify(currentFilters)
        );
      }
      // Fallback: check if we have any active filters
      return Object.values(currentFilters).some((value) => {
        if (typeof value === "string") {
          return value !== "all" && value.trim().length > 0;
        }
        return !!value;
      });
    }

    const viewFilters = selectedView.filters as TFilters;

    // Deep equality check
    return JSON.stringify(viewFilters) !== JSON.stringify(currentFilters);
  }, [selectedView, currentFilters, defaultFilters]);

  // Handler to apply a saved view
  const handleApplyView = useCallback(
    (viewId: string | null) => {
      if (applyingViewRef.current) return;

      // Update URL state
      setUrlState({ viewId: viewId ?? undefined });

      if (!viewId) {
        // If clearing view, call onApplyView with default/reset filters
        if (onApplyView) {
          onApplyView(defaultFilters ?? ({} as TFilters));
        }
        return;
      }

      const view = allViews.find((v) => v.id === viewId);
      if (!view || !onApplyView) return;

      applyingViewRef.current = true;
      try {
        const viewFilters = view.filters as TFilters;
        onApplyView(viewFilters);
      } finally {
        // Reset after a brief delay to allow state updates to complete
        setTimeout(() => {
          applyingViewRef.current = false;
        }, 100);
      }
    },
    [allViews, onApplyView, defaultFilters, setUrlState],
  );

  // Handler to save current view
  const handleSaveView = useCallback(
    async (name?: string) => {
      if (typeof window === "undefined" || !currentFilters) return;

      // Prompt for name if not provided
      let viewName = name;
      if (!viewName) {
        const input = window.prompt(
          "Name this view",
          selectedView?.name ?? `New ${viewType} view`,
        );
        if (!input) return;
        viewName = input.trim();
        if (!viewName) return;
      }

      try {
        if (selectedViewIdFromUrl && selectedView) {
          // Update existing view
          upsertView(viewName, currentFilters, selectedViewIdFromUrl);
        } else {
          // Create new view
          const newViewId = upsertView(viewName, currentFilters);

          // Update URL with new view ID
          setUrlState({ viewId: newViewId });
        }
      } catch (error) {
        logger.error("Failed to save view", { error, viewType });
      }
    },
    [
      currentFilters,
      selectedViewIdFromUrl,
      selectedView,
      viewType,
      upsertView,
      setUrlState,
    ],
  );

  // Handler to delete current view
  const handleDeleteView = useCallback(async () => {
    if (!selectedViewIdFromUrl || !selectedView) return;

    try {
      removeView(selectedViewIdFromUrl);

      // Clear URL state
      setUrlState({ viewId: undefined });

      // Clear filters by applying default/reset filters
      if (onApplyView) {
        onApplyView(defaultFilters ?? ({} as TFilters));
      }
    } catch (error) {
      logger.error("Failed to delete view", { error, viewType });
    }
  }, [
    selectedViewIdFromUrl,
    selectedView,
    removeView,
    setUrlState,
    onApplyView,
    defaultFilters,
    viewType,
  ]);

  // Auto-apply default view on mount
  useEffect(() => {
    if (!autoApplyDefault || applyingViewRef.current) return;

    if (allViews.length > 0 && !selectedViewIdFromUrl && enabled) {
      const defaultView = allViews.find((view) => view.is_default);
      if (defaultView) {
        handleApplyView(defaultView.id);
      }
    }
  }, [
    autoApplyDefault,
    allViews,
    selectedViewIdFromUrl,
    enabled,
    handleApplyView,
  ]);

  // Auto-apply view when selectedViewId changes from URL
  useEffect(() => {
    if (!syncWithUrl || applyingViewRef.current || !selectedViewIdFromUrl)
      return;

    const view = allViews.find((v) => v.id === selectedViewIdFromUrl);
    if (!view || !onApplyView) return;

    applyingViewRef.current = true;
    try {
      const viewFilters = view.filters as TFilters;
      onApplyView(viewFilters);
    } finally {
      setTimeout(() => {
        applyingViewRef.current = false;
      }, 100);
    }
  }, [selectedViewIdFromUrl, allViews, onApplyView, syncWithUrl]);

  return {
    views: allViews,
    selectedViewId: selectedViewIdFromUrl,
    selectedView,
    isDirty,
    isLoading: false,
    error: null,
    createView: async (input: {
      name: string;
      filters: Record<string, unknown>;
      isOrgPreset?: boolean;
    }) => {
      const id = upsertView(input.name, input.filters as TFilters);
      return { view_id: id };
    },
    updateView: async (
      viewId: string,
      input: {
        name?: string;
        filters?: Record<string, unknown>;
        isOrgPreset?: boolean;
      },
    ) => {
      if (input.name || input.filters) {
        upsertView(
          input.name ?? selectedView?.name ?? "Untitled",
          (input.filters ?? currentFilters ?? {}) as TFilters,
          viewId,
        );
      }
      return { view_id: viewId };
    },
    deleteView: async (viewId: string) => {
      removeView(viewId);
      return viewId;
    },
    handleApplyView,
    handleSaveView,
    handleDeleteView,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  };
}
